/**
 * WorkingHoursManagerView — Plano 2 (setup) peça 3: HORÁRIO de funcionamento recorrente.
 *
 * O horário é POR-RECURSO (o recurso É o calendário — decisão D1 do Bloco I). O operador
 * escolhe um recurso e define suas janelas de trabalho por dia da semana (ex.: seg–sex 9–18,
 * sáb 9–13). O motor de disponibilidade só oferece horários dentro dessas janelas (menos os
 * períodos fechados). Sem update no modelo: editar uma janela = remover + adicionar.
 *
 * Backend já pronto (`AgendaManagementService` + `POST/GET/DELETE /v1/scheduling/working-hours`);
 * aqui é só a UI. Gated `scheduling-native` (na página). O período de validade ("abre set/out")
 * fica para um follow-up — o MVP entrega a janela recorrente perpétua.
 */
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useWorkingHours, useWorkingHoursActions } from '../../hooks/use-scheduling';
import { ApiError, type SchedulingResourceWire, type SchedulingWorkingHoursWire } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import type { JSX } from 'react';

const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;

export interface WorkingHoursManagerViewProps {
  resources: SchedulingResourceWire[];
  canManage: boolean;
}

function minuteToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}

function hhmmToMinute(v: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(v);
  if (match === null) return null;
  const h = Number.parseInt(match[1] ?? '', 10);
  const min = Number.parseInt(match[2] ?? '', 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** DD/MM a partir de um ISO datetime (fatia UTC — determinístico, sem drift de fuso). */
function isoToDDMM(iso: string): string {
  const ymd = iso.slice(0, 10); // 'YYYY-MM-DD'
  const [, mm, dd] = ymd.split('-');
  return `${dd ?? '??'}/${mm ?? '??'}`;
}

/** Selo de validade "abre set/out" de uma janela. null = permanente (sem selo). */
function validityLabel(h: SchedulingWorkingHoursWire): string | null {
  const from = h.effectiveFrom !== null ? isoToDDMM(h.effectiveFrom) : null;
  const until = h.effectiveUntil !== null ? isoToDDMM(h.effectiveUntil) : null;
  if (from !== null && until !== null) return `${from} – ${until}`;
  if (from !== null) return `a partir de ${from}`;
  if (until !== null) return `até ${until}`;
  return null;
}

function toast(variant: 'success' | 'error' | 'info', title: string, description?: string): void {
  useToastStore.getState().push({
    variant,
    title,
    ...(description !== undefined && { description }),
    durationMs: variant === 'error' ? 6000 : 3500,
  });
}

export function WorkingHoursManagerView({ resources, canManage }: WorkingHoursManagerViewProps): JSX.Element {
  const [resourceId, setResourceId] = useState(resources[0]?.id ?? '');

  if (resources.length === 0) {
    return (
      <div className="flex flex-col gap-5 px-6 py-5">
        <div
          className="rounded-md px-4 py-8 text-center text-sm"
          style={{ border: '1px dashed var(--border-default)', color: 'var(--fg-3)' }}
        >
          Cadastre um recurso na aba <strong>Recursos</strong> antes de definir o horário de funcionamento.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div
        className="rounded-md px-4 py-3 text-sm"
        role="note"
        style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}
      >
        O horário de funcionamento é por recurso. A IA só oferece agendamentos dentro destas janelas
        (descontados os períodos fechados). Um dia pode ter mais de uma janela — ex.: manhã e tarde
        com intervalo de almoço.
      </div>

      <label className="block max-w-xs">
        <span className="mb-1 block text-sm font-medium">Recurso</span>
        <select
          className="w-full rounded border px-2 py-1"
          style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
          aria-label="Recurso"
        >
          {resources.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </label>

      {resourceId !== '' && <WorkingHoursForResource resourceId={resourceId} canManage={canManage} />}
    </div>
  );
}

function WorkingHoursForResource({ resourceId, canManage }: { resourceId: string; canManage: boolean }): JSX.Element {
  const hoursQ = useWorkingHours(resourceId);
  const actions = useWorkingHoursActions();
  const [day, setDay] = useState('1'); // segunda
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('18:00');
  // A2 — validade opcional ("abre set/out"). '' = janela permanente. Datas YYYY-MM-DD.
  const [effFrom, setEffFrom] = useState('');
  const [effUntil, setEffUntil] = useState('');

  const startMin = hhmmToMinute(start);
  const endMin = hhmmToMinute(end);
  // Comparação lexicográfica de YYYY-MM-DD é válida como ordenação de datas.
  const validityValid = !(effFrom !== '' && effUntil !== '' && effUntil < effFrom);
  const valid = startMin !== null && endMin !== null && endMin > startMin && validityValid;
  const busy = actions.create.isPending || actions.remove.isPending;

  const hours = hoursQ.data?.data ?? [];
  const byDay = (d: number): SchedulingWorkingHoursWire[] =>
    hours.filter((h) => h.dayOfWeek === d).sort((a, b) => a.startMinute - b.startMinute);

  const onAdd = (): void => {
    if (!valid || busy || startMin === null || endMin === null) return;
    // Validade: enviada só quando preenchida. `from` no início do dia, `until` no fim do dia
    // (granularidade de dia — a temporada é um limite de dia, não de hora). Backend valida ordem.
    actions.create.mutate(
      {
        resourceId,
        dayOfWeek: Number.parseInt(day, 10),
        startMinute: startMin,
        endMinute: endMin,
        ...(effFrom !== '' && { effectiveFrom: `${effFrom}T00:00:00.000Z` }),
        ...(effUntil !== '' && { effectiveUntil: `${effUntil}T23:59:59.999Z` }),
      },
      {
        onSuccess: () => {
          toast('success', 'Janela adicionada');
          setEffFrom('');
          setEffUntil('');
        },
        onError: (err) => toast('error', err instanceof ApiError ? 'Não foi possível adicionar a janela.' : 'Falha ao adicionar.'),
      },
    );
  };

  const onRemove = (h: SchedulingWorkingHoursWire): void => {
    actions.remove.mutate(
      { id: h.id },
      {
        onSuccess: () => toast('success', 'Janela removida'),
        onError: () => toast('error', 'Falha ao remover a janela.'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {canManage && (
        <div
          className="flex flex-wrap items-end gap-3 rounded-md px-4 py-3"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}
        >
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Dia</span>
            <select
              className="rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={day}
              onChange={(e) => setDay(e.target.value)}
              aria-label="Dia da semana"
            >
              {DAY_LABELS.map((label, i) => (
                <option key={label} value={String(i)}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Início</span>
            <input
              type="time"
              className="rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={start}
              onChange={(e) => setStart(e.target.value)}
              aria-label="Horário de início"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Fim</span>
            <input
              type="time"
              className="rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              aria-label="Horário de fim"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Válido a partir de</span>
            <input
              type="date"
              className="rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={effFrom}
              onChange={(e) => setEffFrom(e.target.value)}
              aria-label="Válido a partir de"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Válido até</span>
            <input
              type="date"
              className="rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={effUntil}
              onChange={(e) => setEffUntil(e.target.value)}
              aria-label="Válido até"
            />
          </label>
          <button type="button" className="btn btn--primary" onClick={onAdd} disabled={!valid || busy}>
            <Plus size={14} aria-hidden /> Adicionar janela
          </button>
          <p className="w-full text-xs" style={{ color: 'var(--fg-3)' }}>
            Deixe a validade em branco para uma janela permanente. Preencha um período para uma
            temporada — ex.: uma sala que só abre de setembro a outubro.
          </p>
        </div>
      )}

      {hoursQ.isLoading && (
        <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
          Carregando horário…
        </p>
      )}

      {!hoursQ.isLoading && (
        <div className="flex flex-col gap-2">
          {DAY_LABELS.map((label, d) => {
            const windows = byDay(d);
            return (
              <div
                key={label}
                className="flex items-center gap-3 rounded-md px-4 py-2"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}
              >
                <span className="w-24 text-sm font-medium" style={{ color: 'var(--fg-2)' }}>
                  {label}
                </span>
                {windows.length === 0 ? (
                  <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                    Fechado
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {windows.map((h) => (
                      <span
                        key={h.id}
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs"
                        style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', color: 'var(--fg-1)' }}
                      >
                        {minuteToHHMM(h.startMinute)}–{minuteToHHMM(h.endMinute)}
                        {validityLabel(h) !== null && (
                          <span
                            className="rounded px-1"
                            title="Período de validade desta janela"
                            style={{ background: 'var(--brand-subtle)', color: 'var(--brand-subtle-fg)', fontSize: '0.6875rem' }}
                          >
                            {validityLabel(h)}
                          </span>
                        )}
                        {canManage && (
                          <button
                            type="button"
                            aria-label={`Remover janela ${label} ${minuteToHHMM(h.startMinute)}–${minuteToHHMM(h.endMinute)}`}
                            onClick={() => onRemove(h)}
                            disabled={busy}
                            style={{ color: 'var(--fg-3)', lineHeight: 0 }}
                          >
                            <Trash2 size={12} aria-hidden />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
